import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowUp, FileText, Loader2, Mic, Paperclip, Plus, Send, Sparkles, Square, Trash2, X } from 'lucide-react';
import { partnerSessionsApi } from '../../../services/partnerSessionsApi';
import type { DraftBody } from '../../../services/partnerSessionsApi';
import type { PartnerSessionDetail, PartnerSessionFile, PartnerSessionMessage, PartnerSessionSummary, SessionPurpose, SessionTargets } from '../../../types/aai';
import { useVoiceRecorder } from '../../../orbit/dossier/useVoiceRecorder';
import { Action, ErrorNote, Loading, Select } from '../../../components/aa';
import { DraftEditor } from './DraftEditor';
import { RecipientDrawer } from './RecipientDrawer';
import { PurposeBar, needsTarget } from './PurposeBar';
import { errorText, plural } from './sessionShared';

/*
 * New session: the partner talks with the session agent about this client
 * (typing, speaking or uploading files) and the agent writes the draft on the
 * right, which the partner edits by hand too. When it reads right, they pick
 * the people and send it.
 */

const ACCEPT = '.pdf,.docx,.md,.txt,.csv,.xlsx,.xls,image/png,image/jpeg,image/gif,image/webp';
/* Hand edits are saved 700 ms after the last keystroke, and flushed before
   every message so the agent always reads what the partner sees. */
const SAVE_DELAY = 700;

const STARTERS = [
  'Draft a short survey for everyone about where their week goes.',
  'Ask the department heads what slows their team down.',
  'Turn the document I upload into a questionnaire.',
];

const bodyOf = (d: PartnerSessionDetail): DraftBody => ({
  title: d.title, description: d.description, kind: d.kind, questions: d.questions,
  purpose: d.purpose || 'general', target_id: d.target_id || '', target_title: d.target_title || '',
});

const EMPTY: DraftBody = { title: '', description: '', kind: 'questionnaire', questions: [], purpose: 'general', target_id: '', target_title: '' };

const sendable = (d: DraftBody | null): boolean => {
  if (!d || !d.title.trim() || d.questions.length === 0) return false;
  if (needsTarget(d.purpose) && !d.target_id) return false;
  return d.questions.every((q) => {
    if (!q.label.trim()) return false;
    if (q.type === 'single' || q.type === 'multi') return (q.options || []).filter((o) => o.trim()).length >= 2;
    if (q.type === 'secret') return d.purpose === 'project' && !!q.env_name;
    return true;
  });
};

const Bubble: React.FC<{ message: PartnerSessionMessage }> = ({ message }) => {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-[16px] rounded-br-[6px] bg-ink px-3.5 py-2.5 text-[13.5px] leading-[20px] text-white">{message.content}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-[9px] bg-accent text-ink" aria-hidden><Sparkles size={14} /></span>
      <div className="min-w-0 max-w-[88%] whitespace-pre-wrap rounded-[16px] rounded-tl-[6px] bg-canvas-inset px-3.5 py-2.5 text-[13.5px] leading-[20px] text-ink">{message.content}</div>
    </div>
  );
};

export const SessionStudio: React.FC<{ clientId: string; onSent: (sessionId: string) => void }> = ({ clientId, onSent }) => {
  const [drafts, setDrafts] = useState<PartnerSessionSummary[]>([]);
  const [current, setCurrent] = useState<PartnerSessionDetail | null>(null);
  const [draft, setDraft] = useState<DraftBody | null>(null);
  const [messages, setMessages] = useState<PartnerSessionMessage[]>([]);
  const [files, setFiles] = useState<PartnerSessionFile[]>([]);
  const [live, setLive] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attached, setAttached] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [sending, setSending] = useState(false);
  const [targets, setTargets] = useState<SessionTargets | null>(null);

  const saveTimer = useRef<number | null>(null);
  const currentId = useRef('');
  const creating = useRef<Promise<string> | null>(null);
  const pending = useRef<DraftBody | null>(null);
  const abort = useRef<AbortController | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const textarea = useRef<HTMLTextAreaElement | null>(null);

  const voice = useVoiceRecorder(useCallback((t: string) => {
    setText((prev) => {
      if (prev.trim()) return `${prev.trim()} ${t}`;
      return t;
    });
    requestAnimationFrame(() => textarea.current?.focus());
  }, []));
  const recording = voice.status === 'recording';
  const transcribing = voice.status === 'transcribing';

  const open = useCallback((d: PartnerSessionDetail) => {
    currentId.current = d.id;
    setCurrent(d);
    setDraft(bodyOf(d));
    setMessages(d.thread);
    setFiles(d.files);
    setLive('');
    setStatus('');
  }, []);

  const loadDrafts = useCallback(async () => {
    const r = await partnerSessionsApi.list(clientId);
    setDrafts(r.data.drafts);
    return r.data.drafts;
  }, [clientId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    loadDrafts()
      .then(async (list) => {
        if (!alive || list.length === 0) return;
        const r = await partnerSessionsApi.get(clientId, list[0].id);
        if (alive) open(r.data);
      })
      .catch((err) => { if (alive) setError(errorText(err, 'Could not load your drafts.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
      abort.current?.abort();
    };
  }, [clientId, loadDrafts, open]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages, live, status]);

  useEffect(() => {
    let alive = true;
    partnerSessionsApi.targets(clientId)
      .then((r) => { if (alive) setTargets(r.data); })
      .catch(() => { if (alive) setTargets({ projects: [], quick_wins: [] }); });
    return () => { alive = false; };
  }, [clientId]);

  const flush = useCallback(async () => {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const body = pending.current;
    pending.current = null;
    if (!body || !currentId.current) return;
    try {
      await partnerSessionsApi.update(clientId, currentId.current, body);
    } catch (err) {
      toast.error(errorText(err, 'Could not save the draft.'));
    }
  }, [clientId]);

  /* The open draft's id, creating the draft on first use. It reads the ref,
     not state: an upload creates the draft and then sends its message in the
     same tick, before state has re-rendered, and reading state there made a
     second, empty draft that the agent then answered from. Concurrent calls
     share one create. */
  const ensure = (): Promise<string> => {
    if (currentId.current) return Promise.resolve(currentId.current);
    if (creating.current) return creating.current;
    creating.current = partnerSessionsApi.create(clientId)
      .then((r) => {
        // A new draft has no thread yet, so the chat keeps what is already on
        // screen (the message that created it) instead of being reset.
        currentId.current = r.data.id;
        setCurrent(r.data);
        setDraft((d) => d || bodyOf(r.data));
        setFiles(r.data.files);
        setDrafts((list) => [r.data, ...list]);
        return r.data.id;
      })
      .finally(() => { creating.current = null; });
    return creating.current;
  };

  const edit = async (next: DraftBody) => {
    setDraft(next);
    pending.current = next;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    if (!currentId.current) {
      try {
        await ensure();
      } catch (err) {
        toast.error(errorText(err, 'Could not start a draft.'));
        return;
      }
    }
    saveTimer.current = window.setTimeout(() => { flush(); }, SAVE_DELAY);
  };

  const ask = async (message: string) => {
    const value = message.trim();
    if (!value || busy) return;
    const body = draft || EMPTY;
    if (needsTarget(body.purpose) && !body.target_id) return;
    setBusy(true);
    setText('');
    setAttached('');
    setMessages((m) => [...m, { role: 'user', content: value }]);
    setLive('');
    setStatus('Thinking');
    let said = '';
    let ended = false;
    try {
      const sessionId = await ensure();
      await flush();
      const controller = new AbortController();
      abort.current = controller;
      await partnerSessionsApi.chat(clientId, sessionId, value, (event) => {
        if (event.type === 'status') setStatus(event.text);
        if (event.type === 'text') {
          said += event.delta;
          if (said.trim()) {
            setLive(said);
            setStatus('');
          }
        }
        if (event.type === 'draft') {
          setDraft(bodyOf(event.draft));
          setDrafts((list) => list.map((d) => {
            if (d.id === event.draft.id) return { ...d, title: event.draft.title };
            return d;
          }));
        }
        if (event.type === 'error') {
          ended = true;
          toast.error(event.message);
        }
        if (event.type === 'done') ended = true;
      }, controller.signal);
      if (said.trim()) setMessages((m) => [...m, { role: 'assistant', content: said.trim() }]);
      if (!ended) toast.error('The agent stopped before it finished. Send your message again.');
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') toast.error('The agent could not answer just now. Try again.');
    } finally {
      setLive('');
      setStatus('');
      setBusy(false);
      abort.current = null;
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const sessionId = await ensure();
      const r = await partnerSessionsApi.upload(clientId, sessionId, file);
      setFiles(r.data.files);
      // Attaching never sends anything: the partner says what to do with
      // the file, and the agent reads every attached file on that message.
      setAttached(file.name);
      requestAnimationFrame(() => textarea.current?.focus());
    } catch (err) {
      toast.error(errorText(err, 'Could not read that file.'));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (f: PartnerSessionFile) => {
    if (!current) return;
    try {
      const r = await partnerSessionsApi.removeFile(clientId, current.id, f.id);
      setFiles(r.data.files);
    } catch (err) {
      toast.error(errorText(err, 'Could not remove the file.'));
    }
  };

  const startNew = async () => {
    await flush();
    currentId.current = '';
    setCurrent(null);
    setDraft(null);
    setMessages([]);
    setFiles([]);
  };

  const switchTo = async (id: string) => {
    await flush();
    try {
      const r = await partnerSessionsApi.get(clientId, id);
      open(r.data);
    } catch (err) {
      toast.error(errorText(err, 'Could not open that draft.'));
    }
  };

  const discard = async () => {
    if (!current) return;
    if (!window.confirm('Delete this draft and its conversation?')) return;
    pending.current = null;
    try {
      const r = await partnerSessionsApi.remove(clientId, current.id);
      setDrafts(r.data.drafts);
      await startNew();
    } catch (err) {
      toast.error(errorText(err, 'Could not delete the draft.'));
    }
  };

  const send = async (emails: string[], remindEveryDays: number | null) => {
    if (!current) return;
    setSending(true);
    try {
      await flush();
      const r = await partnerSessionsApi.send(clientId, current.id, emails, remindEveryDays);
      toast.success(`Sent to ${plural(r.data.added.length, 'person', 'people')}.`);
      setChoosing(false);
      await loadDrafts();
      await startNew();
      onSent(current.id);
    } catch (err) {
      toast.error(errorText(err, 'Could not send the session.'));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(text);
    }
  };

  if (loading) return <Loading label="Loading your drafts" />;
  if (error) return <ErrorNote message={error} />;

  const working = busy || uploading;
  let placeholder = 'Tell the agent what you want to learn, and from whom…';
  if (attached) placeholder = `${attached} is attached. Tell the agent what to build from it…`;
  if (recording) placeholder = 'Listening… tap stop when you are done';
  if (transcribing) placeholder = 'Transcribing…';
  let micLabel = 'Speak your message';
  let micTone = 'text-ink-muted hover:bg-canvas hover:text-ink';
  if (recording) {
    micLabel = 'Stop recording';
    micTone = 'bg-danger text-white';
  }
  const shownDraft = draft || EMPTY;
  const setPurpose = (purpose: SessionPurpose, targetId: string, targetTitle: string) => edit({ ...shownDraft, purpose, target_id: targetId, target_title: targetTitle });
  const waitingForTarget = needsTarget(shownDraft.purpose) && !shownDraft.target_id;
  if (waitingForTarget) placeholder = 'Pick what this session is for first';
  const project = targets?.projects.find((p) => p.id === shownDraft.target_id);
  const keyNames = (project?.needs || []).filter((n) => n.kind === 'secret' || n.kind === 'connection').map((n) => n.key);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {drafts.length > 0 && (
            <Select value={current?.id || ''} onChange={(e) => { if (e.target.value) switchTo(e.target.value); }} aria-label="Open a draft" className="!h-9 !w-[260px] !text-[12.5px]">
              {!current && <option value="">New draft</option>}
              {drafts.map((d) => <option key={d.id} value={d.id}>{d.title || 'Untitled draft'}</option>)}
            </Select>
          )}
          <Action size="sm" variant="outline" icon={<Plus size={14} />} onClick={startNew} disabled={working}>New draft</Action>
          {current && <Action size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={discard} disabled={working}>Delete draft</Action>}
        </div>
        <Action icon={<Send size={14} />} onClick={async () => { await flush(); setChoosing(true); }} disabled={!sendable(draft) || working}>Choose people and send</Action>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section className="flex h-[680px] flex-col overflow-hidden rounded-[16px] border border-line bg-white" aria-label="Session agent">
          <header className="flex items-center gap-2.5 border-b border-line px-4 py-3">
            <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-accent text-ink" aria-hidden><Sparkles size={15} /></span>
            <div className="min-w-0">
              <b className="block text-[14px] leading-[18px] text-ink">Session agent</b>
              <small className="block text-[11.5px] text-ink-muted">Knows this client: people, tools, skills, projects and what was already asked</small>
            </div>
          </header>
          <PurposeBar purpose={shownDraft.purpose} targetId={shownDraft.target_id} targets={targets} disabled={working} onChange={setPurpose} />
          <div ref={scroller} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && !busy && waitingForTarget && (
              <div className="py-6 text-center">
                <strong className="block text-[15px] text-ink">Which one is it for?</strong>
                <small className="mx-auto mt-1 block max-w-[42ch] text-[12.5px] text-ink-muted">Pick it above. The agent then reads what it already knows about it, and what it still needs.</small>
              </div>
            )}
            {messages.length === 0 && !busy && !waitingForTarget && (
              <div className="py-6 text-center">
                <strong className="block text-[15px] text-ink">What do you want to find out?</strong>
                <small className="mx-auto mt-1 block max-w-[42ch] text-[12.5px] text-ink-muted">Describe it, speak it, or upload a document. The agent drafts the questions on the right.</small>
                <div className="mt-4 flex flex-col items-center gap-2">
                  {STARTERS.map((s) => (
                    <button key={s} type="button" onClick={() => ask(s)} className="rounded-full border border-line bg-white px-3.5 py-1.5 text-[12.5px] text-ink-muted hover:border-ink hover:text-ink">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} message={m} />)}
            {live.trim() && <Bubble message={{ role: 'assistant', content: live.trim() }} />}
            {status && (
              <div className="flex items-center gap-2 pl-9 text-[12px] text-ink-muted">
                <Loader2 size={13} className="animate-spin" aria-hidden /> {status}…
              </div>
            )}
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-line-soft px-4 py-2">
              {files.map((f) => (
                <span key={f.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-canvas-inset py-1 pl-2.5 pr-1 text-[11.5px] font-semibold text-ink">
                  <FileText size={12} aria-hidden />
                  <span className="truncate">{f.name}</span>
                  <button type="button" aria-label={`Remove ${f.name}`} onClick={() => removeFile(f)} disabled={working} className="grid h-5 w-5 place-items-center rounded-full text-ink-dim hover:bg-white hover:text-ink"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="border-t border-line p-3">
            <textarea
              ref={textarea}
              value={text}
              rows={2}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
              readOnly={recording || transcribing}
              disabled={working || waitingForTarget}
              placeholder={placeholder}
              aria-label="Message the session agent"
              className="block max-h-[160px] w-full resize-none border-0 bg-transparent px-1 py-1 text-[13.5px] leading-[20px] text-ink outline-none placeholder:text-ink-soft disabled:cursor-not-allowed"
            />
            <div className="mt-1.5 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Upload a file" title="Upload a file" onClick={() => fileInput.current?.click()} disabled={working || recording || waitingForTarget} className="grid h-9 w-9 place-items-center rounded-full text-ink-muted hover:bg-canvas hover:text-ink disabled:opacity-40">
                  {uploading && <Loader2 size={17} className="animate-spin" />}
                  {!uploading && <Paperclip size={17} />}
                </button>
                <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) upload(f);
                }} />
                <button type="button" aria-label={micLabel} title={micLabel} onClick={() => { if (recording) voice.stop(); else if (!transcribing) voice.start(); }} disabled={working || transcribing || waitingForTarget} className={`grid h-9 w-9 place-items-center rounded-full disabled:opacity-40 ${micTone}`}>
                  {transcribing && <Loader2 size={17} className="animate-spin" />}
                  {!transcribing && recording && <Square size={14} />}
                  {!transcribing && !recording && <Mic size={17} />}
                </button>
                {voice.error && <span className="text-[11.5px] text-danger">{voice.error}</span>}
              </div>
              <button type="button" aria-label="Send" onClick={() => ask(text)} disabled={!text.trim() || working || recording || transcribing || waitingForTarget} className="grid h-9 w-9 place-items-center rounded-full bg-ink text-white disabled:opacity-30">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {!busy && <ArrowUp size={16} />}
              </button>
            </div>
          </div>
        </section>

        <section className="h-[680px] overflow-y-auto rounded-[16px] border border-line bg-canvas p-5" aria-label="Session draft">
          <p className="m-0 mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-dim">Draft · review and edit</p>
          <DraftEditor draft={shownDraft} disabled={busy} keyNames={keyNames} onChange={edit} />
        </section>
      </div>

      {choosing && current && draft && (
        <RecipientDrawer clientId={clientId} title={draft.title} sending={sending} onClose={() => setChoosing(false)} onSend={send} />
      )}
    </div>
  );
};

export default SessionStudio;
